import PptxGenJS from 'pptxgenjs';
import Highcharts from 'highcharts';
import { ForecastDecileModel, ForecastDecileRowModel, ForecastModel } from '../../../../Generated/Mark-It-View-Api';

interface ForecastExportOptions {
  productName: string;
  forecasts: ForecastModel[];
  matrixTable: ForecastDecileModel | null;
  selectedPercentiles: Set<number>;
  matrixConfig: { color: string; textColor: string; tableTextColor: string }[];
  viewMoreInTable: boolean;
  averageRow: Record<string, any>[];
  chartRef: Highcharts.Chart | null;
  currency?: string;
}

function formatNumber(value: any, decimals: number): string {
  if (value == null) {
    return '';
  }
  const number = Number(value);
  if (isNaN(number)) {
    return '';
  }
  return number.toFixed(decimals);
}

function toHex6(color: string): string {
  const hex = color.replace('#', '');
  if (hex.length === 3) {
    return hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  return hex;
}

export function svgToBase64Png(svgMarkup: string, width: number, height: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width * 2;
    canvas.height = height * 2;
    const context = canvas.getContext('2d');
    if (!context) {
      reject(new Error('Could not get canvas context'));
      return;
    }
    context.scale(2, 2);

    const image = new Image();
    image.onload = () => {
      context.drawImage(image, 0, 0, width, height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.onerror = () => reject(new Error('Failed to load SVG as image'));
    image.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgMarkup)));
  });
}

export async function exportForecastToPowerPoint(options: ForecastExportOptions): Promise<void> {
  const { productName, matrixTable, matrixConfig, chartRef, currency = '' } = options;

  const presentation = new PptxGenJS();
  presentation.layout = 'LAYOUT_WIDE';
  const slide = presentation.addSlide();

  // Chart as image from Highcharts SVG
  if (chartRef) {
    const chartWidth = 816;
    const chartHeight = 624;

    // Temporarily resize chart to export dimensions, get SVG, then restore
    const originalWidth = chartRef.chartWidth;
    const originalHeight = chartRef.chartHeight;
    chartRef.setSize(chartWidth, chartHeight, false);
    const svgElement = chartRef.container.querySelector('svg');
    let svgMarkup = '';
    if (svgElement) {
      svgMarkup = new XMLSerializer().serializeToString(svgElement);
    }
    chartRef.setSize(originalWidth, originalHeight, false);

    if (svgMarkup) {
      const chartImage = await svgToBase64Png(svgMarkup, chartWidth, chartHeight);
      slide.addImage({
        data: chartImage,
        x: 0.3,
        y: 0.5,
        w: 8.5,
        h: 6.5,
      });
    }
  }

  // Price Matrix table on the right
  if (matrixTable?.rows) {
    const tableRows: PptxGenJS.TableRow[] = [];

    // Top header row: left cell blank, right cell is 'Annual (CURRENCY)', default borders
    tableRows.push([
      { text: '', options: {} },
      { text: `Annual${currency ? ` (${currency})` : ''}`, options: { bold: true, fontSize: 10, align: 'right', color: '000000' } },
    ]);

    // Header (restore background color for Percentile, blue fill only for header)
    tableRows.push([
      { text: 'Percentile', options: { bold: true, fontSize: 8, color: 'FFFFFF', fill: { color: '1B3A5C' }, align: 'left' } },
      { text: `Range${currency ? ` (${currency})` : ''}`, options: { bold: true, fontSize: 8, color: 'FFFFFF', fill: { color: '1B3A5C' }, align: 'right' } },
    ]);

    // Mean & Median (show currency)
    if (matrixTable.mean != null) {
      tableRows.push([
        { text: 'Mean', options: { bold: true, fontSize: 8, align: 'left', color: '000000' } },
        { text: `${formatNumber(matrixTable.mean, 4)}${currency ? ` ${currency}` : ''}`, options: { fontSize: 8, align: 'right', color: '000000' } },
      ]);
    }
    if (matrixTable.median != null) {
      tableRows.push([
        { text: 'Median', options: { bold: true, fontSize: 8, align: 'left', color: '000000' } },
        { text: `${formatNumber(matrixTable.median, 4)}${currency ? ` ${currency}` : ''}`, options: { fontSize: 8, align: 'right', color: '000000' } },
      ]);
    }

    // Matrix rows (all bands with colors, no blue fill for range)
    matrixTable.rows.forEach((row: ForecastDecileRowModel, index: number) => {
      const fillColor = toHex6(matrixConfig[index]?.color ?? '#FFFFFF');
      // Always use black text for table
      const fontColor = '000000';
      const rangeText = `${formatNumber(row.lowValue, 3)} - ${formatNumber(row.highValue, 3)}${currency ? ` ${currency}` : ''}`;

      tableRows.push([
        { text: row.label ?? '', options: { fontSize: 8, bold: true, align: 'left', fill: { color: fillColor }, color: fontColor } },
        { text: rangeText, options: { fontSize: 8, align: 'right', color: fontColor } },
      ]);
    });

    slide.addTable(tableRows, {
      x: 9.0,
      y: 1.2,
      w: 3.8,
      colW: [1.8, 2.0],
      fontSize: 9,
      rowH: 0.38,
      valign: 'middle',
      border: { type: 'solid', pt: 0.5, color: 'CCCCCC' },
    });
  }

  await presentation.writeFile({ fileName: `${productName} Forecast.pptx` });
}
